// src/pages/problem-bank/ProblemNew.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import {
  ArrowLeft,
  Building2,
  FileText,
  DollarSign,
  Target,
  CheckCircle,
  Upload,
  Plus,
  X,
  Save,
  AlertTriangle,
  Briefcase,
  TrendingUp,
  Shield,
  Clock,
  Zap,
  Package,
  Truck,
  Settings as SettingsIcon,
  Users,
  MapPin,
  Phone,
  Mail,
  User,
  Calendar,
  Info,
  HelpCircle,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';

// Industry sectors from your SOP
const INDUSTRY_SECTORS = [
  { value: 'fmcg', label: 'FMCG' },
  { value: 'agro_processing', label: 'Agro-processing' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'pharmaceutical', label: 'Pharmaceutical' },
  { value: 'textile', label: 'Textile' },
  { value: 'plastics', label: 'Plastics' },
  { value: 'other', label: 'Other' },
];

// Problem categories
const PROBLEM_CATEGORIES = [
  { value: 'downtime', label: 'Downtime', icon: Clock },
  { value: 'energy_waste', label: 'Energy Waste', icon: Zap },
  { value: 'inventory_shrinkage', label: 'Inventory Shrinkage', icon: Package },
  { value: 'quality_control', label: 'Quality Control', icon: CheckCircle },
  { value: 'logistics', label: 'Logistics', icon: Truck },
  { value: 'maintenance', label: 'Maintenance', icon: SettingsIcon },
  { value: 'labor_efficiency', label: 'Labor Efficiency', icon: Users },
  { value: 'other', label: 'Other', icon: AlertTriangle },
];

// Solution types
const SOLUTION_TYPES = [
  { value: 'hardware', label: 'Hardware', icon: Cpu },
  { value: 'software', label: 'Software', icon: FileText },
  { value: 'service', label: 'Service', icon: Briefcase },
  { value: 'hybrid', label: 'Hybrid', icon: TrendingUp },
];

// Step configurations
const STEPS = [
  { number: 1, title: 'Basic Information', description: 'Enter the basic details about the problem', icon: Info, color: 'blue' },
  { number: 2, title: 'Anchor & Loss Quantification', description: 'Quantify the financial impact and anchor details', icon: DollarSign, color: 'green' },
  { number: 3, title: 'Root Cause Analysis', description: 'Analyze the root cause of the problem', icon: Target, color: 'purple' },
  { number: 4, title: 'Solution Potential', description: 'Describe the proposed solution', icon: TrendingUp, color: 'orange' },
  { number: 5, title: 'Evidence & Verification', description: 'Upload evidence and supporting documents', icon: Upload, color: 'indigo' },
];

function Cpu(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><rect x="9" y="9" width="6" height="6"></rect><line x1="9" y1="1" x2="9" y2="4"></line><line x1="15" y1="1" x2="15" y2="4"></line><line x1="9" y1="20" x2="9" y2="23"></line><line x1="15" y1="20" x2="15" y2="23"></line><line x1="20" y1="9" x2="23" y2="9"></line><line x1="20" y1="14" x2="23" y2="14"></line><line x1="1" y1="9" x2="4" y2="9"></line><line x1="1" y1="14" x2="4" y2="14"></line></svg>;
}

export default function ProblemNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    title: '',
    source: 'direct_outreach',
    industrySector: '',
    description: '',
    
    anchorCompany: {
      name: '',
      location: '',
      plantDepartment: '',
      contactPerson: {
        name: '',
        title: '',
        email: '',
        phone: '',
        isDecisionMaker: false,
      },
    },

    quantification: {
      totalLossPerMonth: 0,
      totalLossPerYear: 0,
      calculationMethodology: '',
      confidenceLevel: 'medium' as 'high' | 'medium' | 'low',
      dataSources: [] as string[],
      assumptions: [] as { assumption: string; value: string; justification: string }[],
    },

    rootCause: {
      category: '',
      details: '',
      fiveWhys: [] as { level: number; question: string; answer: string }[],
    },

    solution: {
      hypothesis: '',
      solutionType: '' as 'hardware' | 'software' | 'service' | 'hybrid',
      keyFeatures: [] as string[],
      mvpFeatures: [] as string[],
      estimatedDevCost: 0,
      estimatedDevTimeline: 0,
      estimatedDeployCost: 0,
      estimatedPriceModel: '',
      marketSizeKenya: 0,
    },

    evidence: {
      photoUrls: [] as string[],
      documentUrls: [] as { type: string; url: string; description: string }[],
    },
  });

  // Temporary state for adding items
  const [newDataSource, setNewDataSource] = useState('');
  const [newKeyFeature, setNewKeyFeature] = useState('');
  const [newMvpFeature, setNewMvpFeature] = useState('');
  const [newAssumption, setNewAssumption] = useState({ assumption: '', value: '', justification: '' });
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newDocument, setNewDocument] = useState({ type: '', url: '', description: '' });

  const totalSteps = 5;
  const currentStepData = STEPS[currentStep - 1];
  const StepIcon = currentStepData.icon;

  // DEBUG: Log user on mount
  useState(() => {
    console.log('🔍 ProblemNew mounted');
    console.log('🔍 Current user:', user);
    console.log('🔍 User ID:', user?.id);
    console.log('🔍 Is authenticated:', !!user);
    console.log('🔍 Supabase client:', supabase);
  });

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    console.log('🚀 SUBMIT STARTED');
    console.log('📝 User object:', user);
    console.log('📝 User ID:', user?.id);
    
    if (!user?.id) {
      console.error('❌ No user ID found! User is not authenticated.');
      toast({
        title: 'Authentication Error',
        description: 'You must be logged in to create a problem.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Generate timestamp for problem ID
      const sectorCode = formData.industrySector?.toUpperCase() || 'GEN';
      const timestamp = Date.now().toString().slice(-4);
      const problemId = `VV-${sectorCode}-${timestamp}`;
      
      console.log('📊 Generated problem ID:', problemId);
      console.log('📊 Selected categories:', selectedCategories);
      console.log('📊 Form data:', JSON.stringify(formData, null, 2));

      const problemData = {
        problem_id: problemId,
        date_discovered: new Date().toISOString().split('T')[0],
        source: formData.source,
        title: formData.title,
        category: selectedCategories,
        industry_sector: formData.industrySector,
        status: 'lead',
        anchor: formData.anchorCompany,
        quantification: {
          ...formData.quantification,
          description: formData.description,
          totalLossPerYear: formData.quantification.totalLossPerMonth * 12,
        },
        root_cause_analysis: formData.rootCause,
        solution_potential: {
          ...formData.solution,
          marketSizeEastAfrica: formData.solution.marketSizeKenya * 3,
        },
        verification: {
          observedFirsthand: false,
          photoVideoEvidence: formData.evidence.photoUrls,
          dataFilesReceived: formData.evidence.documentUrls,
          anchorAcknowledgment: {
            obtained: false,
          },
          anchorUrgency: 3,
          willingToPay: 'maybe',
          loiPotential: 3,
        },
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('📤 Final problem data to insert:', JSON.stringify(problemData, null, 2));

      // Direct Supabase insert with detailed logging
      console.log('🔄 Attempting Supabase insert...');
      
      const { data, error } = await supabase
        .from('problem_bank')
        .insert(problemData)
        .select();

      if (error) {
        console.error('❌ Supabase error object:', error);
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error details:', error.details);
        console.error('❌ Error hint:', error.hint);
        throw error;
      }

      console.log('✅ Supabase insert successful!');
      console.log('✅ Returned data:', data);
      
      toast({
        title: 'Success!',
        description: `Problem "${formData.title}" created successfully`,
      });
      
      // Navigate based on user role
      if (user?.role === 'senior_analyst') {
        console.log('🔀 Navigating to admin problem bank');
        navigate('/admin/problem-bank');
      } else {
        console.log('🔀 Navigating to problem bank');
        navigate('/problem-bank');
      }
      
    } catch (error: any) {
      console.error('❌❌❌ CATASTROPHIC ERROR ❌❌❌');
      console.error('Error object:', error);
      console.error('Error message:', error?.message);
      console.error('Error stack:', error?.stack);
      
      toast({
        title: 'Error Creating Problem',
        description: error?.message || 'Failed to create problem. Check console for details.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addDataSource = () => {
    if (newDataSource.trim()) {
      setFormData({
        ...formData,
        quantification: {
          ...formData.quantification,
          dataSources: [...formData.quantification.dataSources, newDataSource.trim()],
        },
      });
      setNewDataSource('');
    }
  };

  const removeDataSource = (index: number) => {
    setFormData({
      ...formData,
      quantification: {
        ...formData.quantification,
        dataSources: formData.quantification.dataSources.filter((_, i) => i !== index),
      },
    });
  };

  const addKeyFeature = () => {
    if (newKeyFeature.trim()) {
      setFormData({
        ...formData,
        solution: {
          ...formData.solution,
          keyFeatures: [...formData.solution.keyFeatures, newKeyFeature.trim()],
        },
      });
      setNewKeyFeature('');
    }
  };

  const removeKeyFeature = (index: number) => {
    setFormData({
      ...formData,
      solution: {
        ...formData.solution,
        keyFeatures: formData.solution.keyFeatures.filter((_, i) => i !== index),
      },
    });
  };

  const addMvpFeature = () => {
    if (newMvpFeature.trim()) {
      setFormData({
        ...formData,
        solution: {
          ...formData.solution,
          mvpFeatures: [...formData.solution.mvpFeatures, newMvpFeature.trim()],
        },
      });
      setNewMvpFeature('');
    }
  };

  const removeMvpFeature = (index: number) => {
    setFormData({
      ...formData,
      solution: {
        ...formData.solution,
        mvpFeatures: formData.solution.mvpFeatures.filter((_, i) => i !== index),
      },
    });
  };

  const addAssumption = () => {
    if (newAssumption.assumption && newAssumption.value) {
      setFormData({
        ...formData,
        quantification: {
          ...formData.quantification,
          assumptions: [...formData.quantification.assumptions, newAssumption],
        },
      });
      setNewAssumption({ assumption: '', value: '', justification: '' });
    }
  };

  const removeAssumption = (index: number) => {
    setFormData({
      ...formData,
      quantification: {
        ...formData.quantification,
        assumptions: formData.quantification.assumptions.filter((_, i) => i !== index),
      },
    });
  };

  const addPhotoUrl = () => {
    if (newPhotoUrl.trim()) {
      setFormData({
        ...formData,
        evidence: {
          ...formData.evidence,
          photoUrls: [...formData.evidence.photoUrls, newPhotoUrl.trim()],
        },
      });
      setNewPhotoUrl('');
    }
  };

  const removePhotoUrl = (index: number) => {
    setFormData({
      ...formData,
      evidence: {
        ...formData.evidence,
        photoUrls: formData.evidence.photoUrls.filter((_, i) => i !== index),
      },
    });
  };

  const addDocument = () => {
    if (newDocument.type && newDocument.url) {
      setFormData({
        ...formData,
        evidence: {
          ...formData.evidence,
          documentUrls: [...formData.evidence.documentUrls, newDocument],
        },
      });
      setNewDocument({ type: '', url: '', description: '' });
    }
  };

  const removeDocument = (index: number) => {
    setFormData({
      ...formData,
      evidence: {
        ...formData.evidence,
        documentUrls: formData.evidence.documentUrls.filter((_, i) => i !== index),
      },
    });
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getProgressPercentage = () => {
    return (currentStep / totalSteps) * 100;
  };

  const isStepValid = () => {
    if (currentStep === 1) {
      return formData.title && formData.industrySector && selectedCategories.length > 0 && formData.description;
    }
    if (currentStep === 2) {
      return formData.anchorCompany.name && formData.anchorCompany.location && 
             formData.anchorCompany.contactPerson.name && formData.anchorCompany.contactPerson.email &&
             formData.quantification.totalLossPerMonth > 0;
    }
    if (currentStep === 3) {
      return formData.rootCause.category && formData.rootCause.details;
    }
    if (currentStep === 4) {
      return formData.solution.hypothesis && formData.solution.solutionType;
    }
    if (currentStep === 5) {
      return formData.quantification.calculationMethodology;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full max-w-full">
      {/* Debug Info Card */}
      <div className="bg-yellow-50 border-b border-yellow-200 p-3 text-sm">
        <div className="max-w-7xl mx-auto">
          <p className="font-semibold text-yellow-800">🔍 Debug Mode Active</p>
          <p className="text-yellow-700 text-xs mt-1">
            User: {user?.name || 'Not logged in'} | ID: {user?.id || 'None'} | Role: {user?.role || 'None'} | Analyst Type: {user?.analyst_type || 'None'}
          </p>
        </div>
      </div>

      {/* Fixed Header with Progress */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="px-4 py-3 max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/problem-bank')} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold">Create New Problem</h1>
                <p className="text-xs text-muted-foreground">
                  Step {currentStep} of {totalSteps}: {currentStepData.title}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              {Math.round(getProgressPercentage())}% Complete
            </Badge>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-2">
            <Progress value={getProgressPercentage()} className="h-1.5" />
          </div>

          {/* Step Navigation Pills */}
          <div className="flex gap-1 mt-3 overflow-x-auto pb-1">
            {STEPS.map((step) => {
              const StepIcon = step.icon;
              const isCompleted = step.number < currentStep;
              const isCurrent = step.number === currentStep;
              
              return (
                <button
                  key={step.number}
                  onClick={() => {
                    if (step.number < currentStep || isStepValid()) {
                      setCurrentStep(step.number);
                    }
                  }}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isCurrent
                      ? 'bg-blue-100 text-blue-700'
                      : isCompleted
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <StepIcon className="h-3 w-3" />
                  <span>{step.title}</span>
                  {isCompleted && <CheckCircle className="h-3 w-3 ml-1" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 max-w-7xl mx-auto">
        {/* Step Header with Icon */}
        <div className="flex items-center gap-3 mb-6">
          <div className={`p-3 rounded-xl bg-${currentStepData.color}-100 text-${currentStepData.color}-600`}>
            <StepIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{currentStepData.title}</h2>
            <p className="text-sm text-muted-foreground">{currentStepData.description}</p>
          </div>
        </div>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    Problem Title <span className="text-red-500">*</span>
                    <HelpCircle className="h-3 w-3 text-muted-foreground" />
                  </Label>
                  <Input
                    placeholder="e.g., Bottling Line Conveyor Misalignment"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="text-base py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    Industry Sector <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.industrySector}
                    onValueChange={(value) => setFormData({ ...formData, industrySector: value })}
                  >
                    <SelectTrigger className="text-base py-3">
                      <SelectValue placeholder="Select industry sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_SECTORS.map(sector => (
                        <SelectItem key={sector.value} value={sector.value}>
                          {sector.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    Problem Categories <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {PROBLEM_CATEGORIES.map(category => {
                      const Icon = category.icon;
                      const isSelected = selectedCategories.includes(category.value);
                      return (
                        <div
                          key={category.value}
                          onClick={() => toggleCategory(category.value)}
                          className={`
                            flex items-center justify-center gap-1.5 p-2 rounded-lg border cursor-pointer transition-all text-sm
                            ${isSelected 
                              ? 'border-blue-500 bg-blue-50 text-blue-700' 
                              : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                            }
                          `}
                        >
                          <Icon className={`h-3.5 w-3.5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                          <span className="text-xs font-medium">{category.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Source</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => setFormData({ ...formData, source: value })}
                  >
                    <SelectTrigger className="text-base py-3">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct_outreach">Direct Outreach</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="audit">Audit</SelectItem>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="online_research">Online Research</SelectItem>
                      <SelectItem value="external_submission">External Submission</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    Problem Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    placeholder="Describe the problem in detail..."
                    rows={12}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="text-base p-4 resize-y min-h-[300px] w-full"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Anchor & Loss Quantification */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Anchor Company Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Company Name *</Label>
                    <Input
                      placeholder="e.g., Kenya Breweries Ltd"
                      value={formData.anchorCompany.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        anchorCompany: { ...formData.anchorCompany, name: e.target.value }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Location *</Label>
                    <Input
                      placeholder="e.g., Nairobi, Athi River"
                      value={formData.anchorCompany.location}
                      onChange={(e) => setFormData({
                        ...formData,
                        anchorCompany: { ...formData.anchorCompany, location: e.target.value }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                </div>

                <Separator />

                <h4 className="text-lg font-semibold flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Contact Person
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name *</Label>
                    <Input
                      placeholder="e.g., Jane Wanjiku"
                      value={formData.anchorCompany.contactPerson.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        anchorCompany: {
                          ...formData.anchorCompany,
                          contactPerson: { ...formData.anchorCompany.contactPerson, name: e.target.value }
                        }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Title *</Label>
                    <Input
                      placeholder="e.g., Plant Manager"
                      value={formData.anchorCompany.contactPerson.title}
                      onChange={(e) => setFormData({
                        ...formData,
                        anchorCompany: {
                          ...formData.anchorCompany,
                          contactPerson: { ...formData.anchorCompany.contactPerson, title: e.target.value }
                        }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email *</Label>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="jane@company.com"
                        value={formData.anchorCompany.contactPerson.email}
                        onChange={(e) => setFormData({
                          ...formData,
                          anchorCompany: {
                            ...formData.anchorCompany,
                            contactPerson: { ...formData.anchorCompany.contactPerson, email: e.target.value }
                          }
                        })}
                        className="text-base py-3 flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone *</Label>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="+254 722 123 456"
                        value={formData.anchorCompany.contactPerson.phone}
                        onChange={(e) => setFormData({
                          ...formData,
                          anchorCompany: {
                            ...formData.anchorCompany,
                            contactPerson: { ...formData.anchorCompany.contactPerson, phone: e.target.value }
                          }
                        })}
                        className="text-base py-3 flex-1"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Loss Quantification
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Monthly Loss (KES) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 11200000"
                      value={formData.quantification.totalLossPerMonth || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        quantification: {
                          ...formData.quantification,
                          totalLossPerMonth: Number(e.target.value),
                          totalLossPerYear: Number(e.target.value) * 12
                        }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Annual Loss (KES)</Label>
                    <Input
                      type="number"
                      value={formData.quantification.totalLossPerYear || ''}
                      disabled
                      className="text-base py-3 bg-muted"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Confidence Level</Label>
                  <Select
                    value={formData.quantification.confidenceLevel}
                    onValueChange={(value: any) => setFormData({
                      ...formData,
                      quantification: { ...formData.quantification, confidenceLevel: value }
                    })}
                  >
                    <SelectTrigger className="text-base py-3">
                      <SelectValue placeholder="Select confidence level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High - Verified with data</SelectItem>
                      <SelectItem value="medium">Medium - Estimated with some data</SelectItem>
                      <SelectItem value="low">Low - Rough estimate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Root Cause Analysis */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Root Cause Category *</Label>
                  <Input
                    placeholder="e.g., Equipment/Machinery, Process/Procedure, etc."
                    value={formData.rootCause.category}
                    onChange={(e) => setFormData({
                      ...formData,
                      rootCause: { ...formData.rootCause, category: e.target.value }
                    })}
                    className="text-base py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Root Cause Details *</Label>
                  <Textarea
                    placeholder="Explain the root cause in detail..."
                    rows={6}
                    value={formData.rootCause.details}
                    onChange={(e) => setFormData({
                      ...formData,
                      rootCause: { ...formData.rootCause, details: e.target.value }
                    })}
                    className="text-base p-4 resize-y min-h-[150px]"
                  />
                </div>

                <div className="space-y-4">
                  <Label className="text-sm font-medium">5 Whys Analysis</Label>
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div key={level} className="ml-4 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                        <Label className="text-sm font-medium block mb-2">Why {level}?</Label>
                        <Textarea
                          placeholder={`Answer for why ${level}...`}
                          value={formData.rootCause.fiveWhys[level - 1]?.answer || ''}
                          onChange={(e) => {
                            const newFiveWhys = [...formData.rootCause.fiveWhys];
                            newFiveWhys[level - 1] = {
                              level,
                              question: `Why ${level}?`,
                              answer: e.target.value,
                            };
                            setFormData({
                              ...formData,
                              rootCause: { ...formData.rootCause, fiveWhys: newFiveWhys },
                            });
                          }}
                          className="text-base p-3 min-h-[80px]"
                          rows={3}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Solution Potential */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Solution Hypothesis *</Label>
                  <Textarea
                    placeholder="Describe your proposed solution..."
                    rows={4}
                    value={formData.solution.hypothesis}
                    onChange={(e) => setFormData({
                      ...formData,
                      solution: { ...formData.solution, hypothesis: e.target.value }
                    })}
                    className="text-base p-4 resize-y min-h-[120px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Solution Type *</Label>
                  <Select
                    value={formData.solution.solutionType}
                    onValueChange={(value: any) => setFormData({
                      ...formData,
                      solution: { ...formData.solution, solutionType: value }
                    })}
                  >
                    <SelectTrigger className="text-base py-3">
                      <SelectValue placeholder="Select solution type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOLUTION_TYPES.map(type => {
                        const Icon = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Key Features</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a key feature"
                        value={newKeyFeature}
                        onChange={(e) => setNewKeyFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addKeyFeature()}
                        className="text-base py-3"
                      />
                      <Button type="button" onClick={addKeyFeature} size="icon" variant="outline" className="h-12 w-12">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.solution.keyFeatures.map((feature, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-2 text-sm">
                          {feature}
                          <button onClick={() => removeKeyFeature(index)} className="ml-2 hover:text-red-500">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">MVP Features</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add an MVP feature"
                        value={newMvpFeature}
                        onChange={(e) => setNewMvpFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addMvpFeature()}
                        className="text-base py-3"
                      />
                      <Button type="button" onClick={addMvpFeature} size="icon" variant="outline" className="h-12 w-12">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.solution.mvpFeatures.map((feature, index) => (
                        <Badge key={index} variant="outline" className="px-3 py-2 text-sm">
                          {feature}
                          <button onClick={() => removeMvpFeature(index)} className="ml-2 hover:text-red-500">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Development Cost (KES)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 400000"
                      value={formData.solution.estimatedDevCost || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        solution: { ...formData.solution, estimatedDevCost: Number(e.target.value) }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Timeline (weeks)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 8"
                      value={formData.solution.estimatedDevTimeline || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        solution: { ...formData.solution, estimatedDevTimeline: Number(e.target.value) }
                      })}
                      className="text-base py-3"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Market Size (Kenya)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 50"
                    value={formData.solution.marketSizeKenya || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      solution: { ...formData.solution, marketSizeKenya: Number(e.target.value) }
                    })}
                    className="text-base py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Pricing Model</Label>
                  <Textarea
                    placeholder="e.g., KES 250,000 installation + KES 200,000/year subscription"
                    rows={3}
                    value={formData.solution.estimatedPriceModel}
                    onChange={(e) => setFormData({
                      ...formData,
                      solution: { ...formData.solution, estimatedPriceModel: e.target.value }
                    })}
                    className="text-base p-4"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Evidence & Verification */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Calculation Methodology *</Label>
                  <Textarea
                    placeholder="Explain step by step how you calculated the loss..."
                    rows={6}
                    value={formData.quantification.calculationMethodology}
                    onChange={(e) => setFormData({
                      ...formData,
                      quantification: {
                        ...formData.quantification,
                        calculationMethodology: e.target.value
                      }
                    })}
                    className="text-base p-4 resize-y min-h-[150px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Data Sources</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Production logs 2025"
                      value={newDataSource}
                      onChange={(e) => setNewDataSource(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addDataSource()}
                      className="text-base py-3"
                    />
                    <Button type="button" onClick={addDataSource} size="icon" variant="outline" className="h-12 w-12">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.quantification.dataSources.map((source, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-2 text-sm">
                        {source}
                        <button onClick={() => removeDataSource(index)} className="ml-2 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Assumptions</Label>
                  <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
                    <Input
                      placeholder="Assumption (e.g., Production rate)"
                      value={newAssumption.assumption}
                      onChange={(e) => setNewAssumption({ ...newAssumption, assumption: e.target.value })}
                      className="text-base py-3"
                    />
                    <Input
                      placeholder="Value (e.g., 8,000 units/hr)"
                      value={newAssumption.value}
                      onChange={(e) => setNewAssumption({ ...newAssumption, value: e.target.value })}
                      className="text-base py-3"
                    />
                    <Input
                      placeholder="Justification"
                      value={newAssumption.justification}
                      onChange={(e) => setNewAssumption({ ...newAssumption, justification: e.target.value })}
                      className="text-base py-3"
                    />
                    <Button type="button" onClick={addAssumption} variant="outline" className="w-full">
                      Add Assumption
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Photo Evidence (Google Drive URLs)</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://drive.google.com/..."
                      value={newPhotoUrl}
                      onChange={(e) => setNewPhotoUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addPhotoUrl()}
                      className="text-base py-3"
                    />
                    <Button type="button" onClick={addPhotoUrl} size="icon" variant="outline" className="h-12 w-12">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Document Evidence</Label>
                  <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
                    <Input
                      placeholder="Document type"
                      value={newDocument.type}
                      onChange={(e) => setNewDocument({ ...newDocument, type: e.target.value })}
                      className="text-base py-3"
                    />
                    <Input
                      placeholder="Google Drive URL"
                      value={newDocument.url}
                      onChange={(e) => setNewDocument({ ...newDocument, url: e.target.value })}
                      className="text-base py-3"
                    />
                    <Input
                      placeholder="Description"
                      value={newDocument.description}
                      onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                      className="text-base py-3"
                    />
                    <Button type="button" onClick={addDocument} variant="outline" className="w-full">
                      Add Document
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Footer */}
        <div className="mt-8 flex justify-between items-center">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1} size="lg" className="gap-2 px-6">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          
          <div className="text-sm text-muted-foreground">
            Step {currentStep} of {totalSteps}
          </div>

          {currentStep < totalSteps ? (
            <Button onClick={handleNext} size="lg" className="gap-2 px-6 bg-blue-600 hover:bg-blue-700" disabled={!isStepValid()}>
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting || !isStepValid()} size="lg" className="gap-2 px-6 bg-green-600 hover:bg-green-700">
              {isSubmitting ? (
                <>Creating...</>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Create Problem
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}